import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import WBFigureView, { isFigureEffectivelyEmpty } from '../components/WBFigure';
import DownloadButton from '../components/DownloadButton';
import ApaReference from '../components/ApaReference';
import { safeText } from '../lib/render';
import { renderAcademicMarkdown } from '../lib/markdown';

export default function DissertationView() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await api.get(`/api/dissertations/${id}`);
        setData(res);
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : 'Failed to load dissertation');
      }
    })();
  }, [id]);

  const chapters = useMemo(() => {
    if (!data?.chapters) return [];
    return Object.keys(data.chapters)
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => {
        const raw = data.chapters[key];
        const content = typeof raw === 'string' ? raw : raw?.content ?? '';
        const title =
          (typeof raw === 'object' && raw?.title) ? raw.title : `Chapter ${key}`;
        const wordCount =
          typeof raw === 'object' && typeof raw.word_count === 'number'
            ? raw.word_count
            : content.split(/\s+/).filter(Boolean).length;
        const n = Number(key) || 0;
        return {
          key: `chapter-${key}`,
          ordinal: `CHAPTER ${numberToWord(n)}`,
          title,
          wordCount,
          content,
        };
      });
  }, [data]);

  if (err) {
    return (
      <div className="main-container">
        <div className="alert alert-danger">{err}</div>
        <Link to="/dashboard" className="btn btn-outline-primary">
          <i className="fas fa-arrow-left me-1"></i>Back to dashboard
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="main-container">
        <div className="text-muted">Loading dissertation…</div>
      </div>
    );
  }

  const isSample = !!data.is_sample;
  const loggedIn = !!user;
  const { content: appendixContent, title: appendixTitle } = extractAppendix(data.appendix);
  const chapterFigures = (chKey) => {
    const raw = chKey.replace('chapter-', '');
    const chNum = String(raw);

    // Prefer the rich figure payload from analysis_results — it carries
    // `type`, `data.{years, values, series1, series2, headers, rows, ...}`
    // so WBFigureView can actually draw a chart. Fall back to the legacy
    // `figures/` registry only when no rich data exists.
    const wb = data.analysis_results?.worldbank_timeseries?.results?.figures;
    const wbList = Array.isArray(wb)
      ? wb
      : (wb && typeof wb === 'object' ? Object.values(wb) : []);
    const matchesChapter = (rawSection) => {
      // Section values come in mixed shapes: "4", "4_1", "4_1_3", "4.1", "4.2.3".
      // Normalise underscores to dots so a single prefix check works for all.
      const section = String(rawSection || '').replace(/_/g, '.');
      return section === chNum || section.startsWith(`${chNum}.`);
    };
    const wbForChapter = wbList
      .filter((f) => f && typeof f === 'object')
      .filter((f) => matchesChapter(f.section))
      .filter((f) => !isDissertationFigureEmpty(f));

    if (wbForChapter.length > 0) {
      return wbForChapter.map((f, i) => [f.figure_id || `wb-${chNum}-${i}`, f]);
    }

    // Legacy fallback — registry-only figures (tables with headers/rows or
    // image-URL figures). Match both "chapter_N" and numeric forms.
    if (!data.figures) return [];
    return Object.entries(data.figures).filter(([, f]) => {
      const sectionLower = String(f?.section || '').toLowerCase();
      const inChapter =
        sectionLower.startsWith(`chapter_${chNum}`) || matchesChapter(f?.section);
      if (!inChapter) return false;
      return !isDissertationFigureEmpty(f);
    });
  };

  return (
    <div className="main-container">
      <div className="row">
        <div className="col-lg-10 mx-auto">
          {isSample && !loggedIn && (
            <div
              className="mb-4 p-4"
              style={{
                background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                color: '#fff',
                borderRadius: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '1rem',
              }}
            >
              <div>
                <h5 className="mb-1 fw-bold">
                  <i className="fas fa-graduation-cap me-2"></i>Want a dissertation like this?
                </h5>
                <p className="mb-0" style={{ opacity: 0.88 }}>
                  Sign up and generate a complete, custom dissertation in minutes.
                </p>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <Link to="/register" className="btn btn-light fw-bold px-4">
                  <i className="fas fa-user-plus me-1"></i>Sign up free
                </Link>
                <Link to="/login" className="btn btn-outline-light px-4">
                  <i className="fas fa-sign-in-alt me-1"></i>Log in
                </Link>
              </div>
            </div>
          )}
          {isSample && loggedIn && (
            <div className="alert alert-info mb-4 d-flex align-items-center gap-2">
              <i className="fas fa-star text-warning"></i>
              <span>
                This is a <strong>sample dissertation</strong> — browse for inspiration, then{' '}
                <Link to="/generate" className="alert-link">
                  generate your own
                </Link>
                .
              </span>
            </div>
          )}

          <div className="card mb-4">
            <div
              style={{
                background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                color: '#fff',
                padding: '1.1rem 1.4rem',
                borderRadius: '12px 12px 0 0',
              }}
            >
              <h3 className="mb-0">
                <i className="fas fa-file-alt me-2"></i>Dissertation
              </h3>
            </div>
            <div className="card-body">
              <h4 className="mb-3">{data.topic || '(untitled)'}</h4>
              <div className="row">
                <div className="col-md-6">
                  {data.research_area && (
                    <p className="mb-1">
                      <strong>Research area:</strong> {data.research_area}
                    </p>
                  )}
                  <p className="mb-1">
                    <strong>Status:</strong>{' '}
                    <span className={`status-badge ${(data.status || 'pending').toLowerCase()}`}>
                      {data.status || 'pending'}
                    </span>
                  </p>
                </div>
                <div className="col-md-6">
                  {data.created_at && (
                    <p className="mb-1">
                      <strong>Created:</strong> {new Date(data.created_at).toLocaleDateString()}
                    </p>
                  )}
                  <p className="mb-1">
                    <strong>ID:</strong> <code>{data.dissertation_id}</code>
                  </p>
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap mt-3 align-items-center">
                <DownloadButton
                  href={`/dissertation/${id}/download/pdf`}
                  filename={`Dissertation_${id}.pdf`}
                  icon="fa-file-pdf"
                  label="Download PDF"
                  className="btn btn-cta"
                />
                <DownloadButton
                  href={`/dissertation/${id}/download/docx`}
                  filename={`Dissertation_${id}.docx`}
                  icon="fa-file-word"
                  label="Download DOCX"
                  className="btn btn-outline-primary"
                />
                <Link to="/dashboard" className="btn btn-outline-secondary">
                  <i className="fas fa-home me-1"></i>Back to dashboard
                </Link>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="fas fa-list me-2" style={{ color: 'var(--primary)' }}></i>
                Table of contents
              </h5>
              <div className="list-group list-group-flush">
                {chapters.map((ch) => (
                  <a
                    key={ch.key}
                    href={`#${ch.key}`}
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    style={{ border: 'none', borderBottom: '1px solid var(--border)' }}
                  >
                    <span className="fw-semibold">
                      {ch.ordinal}: {ch.title.toUpperCase()}
                    </span>
                    <span className="badge bg-info text-dark">{ch.wordCount} words</span>
                  </a>
                ))}
                {data.references?.length > 0 && (
                  <a
                    href="#references"
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    style={{ border: 'none', borderBottom: '1px solid var(--border)' }}
                  >
                    <span className="fw-semibold">References</span>
                    <span className="badge bg-info text-dark">{data.references.length} sources</span>
                  </a>
                )}
                {appendixContent && (
                  <a
                    href="#appendix"
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    style={{ border: 'none' }}
                  >
                    <span className="fw-semibold">{appendixTitle || 'Appendix'}</span>
                    <i className="fas fa-clipboard-list text-muted"></i>
                  </a>
                )}
              </div>
            </div>
          </div>

          {chapters.map((ch) => {
            // Build a figures-by-section-number index for THIS chapter so we
            // can splice each figure group in right under its section heading,
            // rather than dumping them all at the end of the chapter.
            const chapterFigsMap = (() => {
              const map = {};
              for (const [figId, fig] of chapterFigures(ch.key)) {
                const raw = fig.section_label || fig.section || '';
                const label = String(raw).replace(/_/g, '.');
                if (!map[label]) map[label] = [];
                map[label].push([figId, fig]);
              }
              return map;
            })();
            const sectionGroups = splitChapterBySection(ch.content);
            const chNum = String(ch.key).replace('chapter-', '');
            return (
              <div key={ch.key} id={ch.key} className="card mb-4">
                <div className="card-body">
                  <h3 className="mb-1" style={{ color: 'var(--primary)' }}>
                    {ch.ordinal}: {ch.title.toUpperCase()}
                  </h3>
                  <small className="text-muted">
                    Word count: {ch.wordCount} •{' '}
                    <span className="text-success">
                      <i className="fas fa-check-circle me-1"></i>Complete
                    </span>
                  </small>
                  <hr />
                  {sectionGroups.map((grp, idx) => {
                    const body = renderContent(grp.body);
                    // Only the EXACT section's figures render here so 4.1
                    // figures don't also appear under 4.1.1.
                    const figs =
                      grp.sectionNumber && chapterFigsMap[grp.sectionNumber]
                        ? chapterFigsMap[grp.sectionNumber]
                        : (!grp.sectionNumber && idx === 0 && chapterFigsMap[chNum])
                        ? chapterFigsMap[chNum]
                        : [];
                    return (
                      <div key={idx} className="mb-4">
                        {grp.sectionNumber && (
                          <h4
                            className="mt-4 mb-3"
                            style={{ color: 'var(--ink)', fontWeight: 700 }}
                          >
                            {grp.sectionNumber} {grp.sectionTitle}
                          </h4>
                        )}
                        {body.html && (
                          <article
                            className="dissertation-article"
                            dangerouslySetInnerHTML={{ __html: body.html }}
                          />
                        )}
                        {figs.length > 0 && (
                          <div className="d-flex flex-column gap-3 mt-3">
                            {figs.map(([figId, fig]) => {
                              const isWB =
                                fig &&
                                (fig.type === 'line_dual' ||
                                  fig.type === 'bar_diff' ||
                                  fig.type === 'scatter' ||
                                  fig.type === 'stat_table' ||
                                  (fig.data &&
                                    (fig.data.years ||
                                      fig.data.series1 ||
                                      fig.data.headers)));
                              return isWB ? (
                                <div key={figId} className="card">
                                  <div className="card-body">
                                    <WBFigureView fig={fig} />
                                  </div>
                                </div>
                              ) : (
                                <FigureCard key={figId} id={figId} fig={fig} />
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Prefer AI-validated (web-searched, APA-grade) references when
              available. Fall back to the legacy plain reference list
              compiled by citation_manager. */}
          {Array.isArray(data.references_validated) && data.references_validated.length > 0 ? (
            <div id="references" className="card mb-4">
              <div className="card-body">
                <h3 className="mb-3" style={{ color: 'var(--primary)' }}>
                  References
                </h3>
                {data.references_stats && (
                  <p className="text-muted small mb-3">
                    {data.references_stats.valid} verified,{' '}
                    {data.references_stats.corrected} corrected,{' '}
                    {data.references_stats.invalid} flagged — checked against
                    live web sources.
                  </p>
                )}
                <ol style={{ lineHeight: 1.8 }}>
                  {data.references_validated.map((r, i) => (
                    <ApaReference key={i} ref={r} />
                  ))}
                </ol>
              </div>
            </div>
          ) : data.references?.length > 0 ? (
            <div id="references" className="card mb-4">
              <div className="card-body">
                <h3 className="mb-3" style={{ color: 'var(--primary)' }}>
                  References
                </h3>
                <ol style={{ lineHeight: 1.8 }}>
                  {data.references.map((r, i) => (
                    <li key={i}>
                      {typeof r === 'string' ? r : r.citation || r.text || JSON.stringify(r)}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          ) : null}

          {appendixContent && (() => {
            const ar = renderContent(appendixContent);
            return (
              <div id="appendix" className="card mb-4">
                <div className="card-body">
                  <h3 className="mb-3" style={{ color: 'var(--primary)' }}>
                    {appendixTitle || 'Appendix'}
                  </h3>
                  <article
                    className="dissertation-article"
                    dangerouslySetInnerHTML={{ __html: ar.html }}
                  />
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// Delegate to the shared academic markdown renderer — it handles inline `##`
// markers, run-in section labels, oversized headings, URL-to-references
// extraction, and cite-marker cleanup. Keeping DissertationView's own ad-hoc
// renderer let `## 1.2 Problem Statement` show up as literal text mid-prose,
// so we now use the one renderer that's in sync with DissertationLive and
// AssignmentView.
function renderContent(raw) {
  if (!raw) return { html: '', references: [] };
  if (typeof raw !== 'string') {
    try {
      const pre = `<pre style="white-space:pre-wrap;font-family:inherit">${String(
        JSON.stringify(raw, null, 2),
      )
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')}</pre>`;
      return { html: pre, references: [] };
    } catch {
      return { html: '', references: [] };
    }
  }
  return renderAcademicMarkdown(raw);
}

// Split a flat chapter-content string into section-sized chunks so each
// section renders as: heading → prose paragraphs → that section's figures.
// Returns [{sectionNumber, sectionTitle, body}, …]. The first chunk may
// have sectionNumber=null (prose before any numbered heading — usually
// the chapter opener).
function splitChapterBySection(content) {
  if (!content || typeof content !== 'string') {
    return [{ sectionNumber: null, sectionTitle: '', body: content || '' }];
  }
  // Expand flat-blob storage (zero newlines + inline `  ## N.N Title  body`)
  // into newline-delimited markdown first.
  let md = content;
  if (!/\n\n/.test(md) && /\s{2,}/.test(md)) {
    md = md.replace(/\s{2,}(#{1,6}\s+\d+(?:\.\d+)*\s+)/g, '\n\n$1');
    md = md.replace(
      /^(#{1,6})\s+(\d+(?:\.\d+)*\s+[^\n]+?)\s{2,}([^\n]+)$/gm,
      '$1 $2\n\n$3',
    );
    md = md.replace(/[ \t]{2,}/g, '\n\n');
  }
  const re = /^#{1,6}\s+(\d+(?:\.\d+)*)\s+([^\n]*)$/gm;
  const chunks = [];
  let lastEnd = 0;
  let pending = null;
  let m;
  while ((m = re.exec(md)) !== null) {
    // Close out the previous section (or the pre-heading intro)
    const bodyText = md.slice(lastEnd, m.index).trim();
    if (pending) {
      pending.body = bodyText;
      chunks.push(pending);
    } else if (bodyText) {
      chunks.push({ sectionNumber: null, sectionTitle: '', body: bodyText });
    }
    // Start the new section — body is everything AFTER this heading line
    pending = {
      sectionNumber: m[1],
      sectionTitle: (m[2] || '').trim(),
      body: '',
    };
    lastEnd = m.index + m[0].length;
  }
  if (pending) {
    pending.body = md.slice(lastEnd).trim();
    chunks.push(pending);
  } else if (md.trim()) {
    chunks.push({ sectionNumber: null, sectionTitle: '', body: md });
  }
  return chunks;
}

// Number → capitalised word form: 1 → "ONE", 2 → "TWO", etc.
// Used for chapter ordinal headings ("CHAPTER ONE: INTRODUCTION").
const _NUMBER_WORDS = [
  '', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE',
  'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN',
  'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN',
];
function numberToWord(n) {
  return _NUMBER_WORDS[n] || String(n);
}

function extractAppendix(raw) {
  if (!raw) return { content: '', title: '' };
  if (typeof raw === 'string') return { content: raw, title: '' };
  if (typeof raw !== 'object') return { content: String(raw), title: '' };
  const title = raw.title || '';
  const content = raw.content || raw.text || raw.body || raw.html || '';
  if (content) return { content, title };
  const metaOnly = Object.keys(raw).every((k) =>
    ['title', 'data_source', 'generated_at', 'is_complete'].includes(k),
  );
  if (metaOnly) return { content: '', title };
  return { content: raw, title };
}

function isDissertationFigureEmpty(fig) {
  if (!fig) return true;
  if (fig.data && (fig.data.headers || fig.data.rows)) {
    return isFigureEffectivelyEmpty(fig);
  }
  const headers = fig.headers ?? [];
  const rows = fig.rows ?? [];
  if (fig.image_url) return false;
  if (fig.type && fig.type !== 'stat_table') return false;
  if (!headers.length || !rows.length) return true;
  const hasAny = rows.some((row) => {
    if (Array.isArray(row)) {
      return row.some((c) => c != null && c !== '' && c !== '—' && c !== '-');
    }
    if (row && typeof row === 'object') {
      return Object.values(row).some(
        (c) => c != null && c !== '' && c !== '—' && c !== '-',
      );
    }
    return false;
  });
  return !hasAny;
}

function FigureCard({ id, fig }) {
  const isTable = (fig.headers && fig.headers.length > 0) || fig.table_type;
  return (
    <div
      className="p-3"
      style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8 }}
    >
      <div
        className="small text-muted text-uppercase"
        style={{ letterSpacing: 1, fontSize: 11 }}
      >
        {id}
      </div>
      <div className="fw-semibold mt-1">{fig.title || '(untitled)'}</div>
      {fig.image_url && !isTable && (
        <img
          src={fig.image_url}
          alt={fig.title || id}
          style={{ maxWidth: '100%', marginTop: 12, borderRadius: 6 }}
        />
      )}
      {isTable && fig.headers && (
        <div className="table-responsive mt-3">
          <table className="table table-sm table-bordered">
            <thead className="table-light">
              <tr>
                {fig.headers.map((h, i) => (
                  <th key={i}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(fig.rows ?? []).map((row, ri) => (
                <tr key={ri}>
                  {Array.isArray(row)
                    ? row.map((cell, ci) => (
                        <td key={ci}>{cell == null ? '—' : String(cell)}</td>
                      ))
                    : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {fig.caption && (
        <div className="text-muted small mt-2" style={{ fontStyle: 'italic' }}>
          {fig.caption}
        </div>
      )}
    </div>
  );
}
